<template>
  <div class="directions-panel">
    <GoogleMapDirectionsContainer :require-email="requireEmail" />
  </div>
</template>

<script>
import GoogleMapDirectionsContainer from '../../../cresiteiq/GoogleMapDirectionsContainer.vue';

export default {
  name: 'DirectionsPanel',
  
  props: {
    requireEmail: {
      type: Boolean,
      default: false
    }
  },
  
  components: {
    GoogleMapDirectionsContainer
  }
};
</script>

<style scoped>
.directions-panel {
  margin-top: 0.5rem;
  background-color: var(--card);
  //border: 1px solid var(--border);
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Clean styling for the directions container */
.directions-panel :deep(.mb-4) {
  margin-bottom: 0;
}

/* Ensure the my-4 class (Calculate Trip Cost button container) is visible */
.directions-panel :deep(.my-4) {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  margin: 1rem 0 !important;
}

.directions-panel :deep(.p-3) {
  padding: 0;
}

/* Remove any background from the container */
.directions-panel :deep(.relative.rounded) {
  background: transparent;
}

/* Style inputs to match theme */
.directions-panel :deep(input[type="text"]),
.directions-panel :deep(input[type="date"]),
.directions-panel :deep(input[type="time"]),
.directions-panel :deep(select) {
  background-color: var(--input);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  color: var(--foreground);
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  width: 100%;
  transition: all 0.2s ease;
}

.directions-panel :deep(input:focus),
.directions-panel :deep(select:focus) {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
}

.directions-panel :deep(input::placeholder) {
  color: var(--muted-foreground);
}

/* Style the Add Stop button */
.directions-panel :deep(.add-stop-link) {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background-color: transparent;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  color: var(--foreground) !important;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  cursor: pointer;
  text-decoration: none;
  margin: 0.5rem 0;
}

.directions-panel :deep(.add-stop-link:hover) {
  background-color: var(--accent);
  border-color: var(--primary);
}

/* Ensure text inside Add Stop button is white in dark theme */
.directions-panel :deep(.add-stop-link span),
.directions-panel :deep(.add-stop-link .text-blue-600) {
  color: var(--foreground) !important;
}

/* Change "Add Destination" text to "Add Stop" */
.directions-panel :deep(.add-stop-link span:contains("Add Destination"))::after {
  content: "Add Stop";
}

.directions-panel :deep(.add-stop-link span:contains("Add Destination")) {
  font-size: 0;
}

.directions-panel :deep(.add-stop-link span:contains("Add Destination"))::after {
  font-size: 0.875rem;
}

/* Style buttons */
.directions-panel :deep(.bg-green-600),
.directions-panel :deep(button[class*="bg-green"]) {
  background-color: var(--primary);
  color: var(--primary-foreground);
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  width: 100%;
  margin-top: 0.5rem;
}

.directions-panel :deep(.bg-green-600:hover:not(:disabled)) {
  opacity: 0.9;
}

/* Ensure the Calculate Trip Cost button container is visible */
.directions-panel :deep(.flex.justify-center) {
  display: flex !important;
  visibility: visible !important;
  justify-content: center;
  margin: 1rem 0 !important;
}

/* Style Calculate Trip Cost button to match Add Stop button */
.directions-panel :deep(.flex.justify-center button),
.directions-panel :deep(button.bg-green-600),
.directions-panel :deep(button[class*="bg-green"]) {
  display: flex !important;
  visibility: visible !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem 1rem;
  background-color: transparent !important;
  color: var(--foreground) !important;
  border: 1px solid var(--border) !important;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  cursor: pointer;
}

.directions-panel :deep(.flex.justify-center button:hover) {
  background-color: var(--accent) !important;
  border-color: var(--primary) !important;
  opacity: 1;
}

/* Ensure icon color matches text */
.directions-panel :deep(.flex.justify-center button svg),
.directions-panel :deep(.flex.justify-center button .fill-current) {
  color: var(--foreground) !important;
  fill: currentColor !important;
}

/* Make sure v-if condition doesn't hide it */
.directions-panel :deep(div[class*="justify-center"]) {
  display: flex !important;
}

/* Style labels */
.directions-panel :deep(label) {
  color: var(--muted-foreground);
  font-size: 0.8125rem;
  font-weight: 500;
  margin-bottom: 0.375rem;
  display: block;
}

/* Style the route details box */
.directions-panel :deep(.border.border-gray-200) {
  background-color: var(--muted);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-top: 0.5rem;
}

/* Style error messages */
.directions-panel :deep(.bg-red-100) {
  background-color: var(--destructive);
  color: var(--destructive-foreground);
  border: 1px solid var(--destructive);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-top: 0.5rem;
}

/* Style the header buttons */
.directions-panel :deep(.flex.items-center.justify-between) {
  position: relative;
}

/* Style the Clear button */
.directions-panel :deep(.flex.items-center.justify-between .text-red-600) {
  display: inline-block !important;
  color: var(--foreground) !important;
  font-size: 0.75rem;
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  transition: all 0.2s ease;
  font-weight: 500;
}

.directions-panel :deep(.flex.items-center.justify-between .text-red-600:hover) {
  text-decoration: underline;
  opacity: 0.7;
}

/* Hide only the close (X) button, not the Clear button */
.directions-panel :deep(.flex.items-center.justify-between button:not(.text-red-600)) {
  display: none;
}

/* Change "Directions" title to "Route Planning" */
.directions-panel :deep(.flex.items-center.justify-between .font-bold) {
  font-size: 0;
}

.directions-panel :deep(.flex.items-center.justify-between .font-bold)::after {
  content: "Route Planning";
  font-size: 1rem;
  font-weight: 600;
  color: var(--foreground);
}

/* Style the marker icons */
.directions-panel :deep(.bg-blue-100) {
  background-color: var(--primary);
  opacity: 0.2;
}

.directions-panel :deep(.text-blue-700) {
  color: var(--primary);
}

/* Style the first stop (origin) with green dot - no text */
.directions-panel :deep(div[class*="flex items-center"]:nth-child(1) .rounded-full),
.directions-panel :deep(div[class*="flex items-center"]:first-child .rounded-full) {
  background-color: #22c55e !important;
  border-color: #22c55e !important;
  color: transparent !important;
  opacity: 1 !important;
  font-size: 0 !important;
  border-width: 2px !important;
}

/* Style the second stop (destination) with red dot - no text */
.directions-panel :deep(div[class*="flex items-center"]:nth-child(2) .rounded-full) {
  background-color: #ef4444 !important;
  border-color: #ef4444 !important;
  color: transparent !important;
  opacity: 1 !important;
  font-size: 0 !important;
  border-width: 2px !important;
}

/* Style waypoint stops (3rd, 4th, 5th, etc.) with yellow dots - no text */
.directions-panel :deep(div[class*="flex items-center"]:nth-child(n+3) .rounded-full) {
  background-color: #eab308 !important;
  border-color: #eab308 !important;
  color: transparent !important;
  opacity: 1 !important;
  font-size: 0 !important;
  border-width: 2px !important;
}

/* Spacing adjustments */
.directions-panel :deep(.flex-col.gap-y-2) {
  gap: 0.75rem;
}

.directions-panel :deep(.mb-2) {
  margin-bottom: 0.5rem;
}

/* Style the remove stop (X) buttons to use theme colors */
.directions-panel :deep(.text-red-600) {
  color: var(--muted-foreground) !important;
  transition: all 0.2s ease;
}

.directions-panel :deep(.text-red-600:hover) {
  color: var(--foreground) !important;
  opacity: 0.8;
}

/* Ensure the X icon uses theme colors */
.directions-panel :deep(.text-red-600 svg),
.directions-panel :deep(.text-red-600 .fill-current) {
  color: var(--muted-foreground) !important;
  fill: currentColor !important;
}

.directions-panel :deep(.text-red-600:hover svg),
.directions-panel :deep(.text-red-600:hover .fill-current) {
  color: var(--foreground) !important;
}
</style>
